/* ImpersonationBanner — Persistent top-of-app banner shown only while an
 * admin is impersonating another user via `Admin → Users → "Login as user"`.
 *
 * Reads `admin_stash_meta` from localStorage (written by `impersonate()` in
 * AuthContext) and surfaces:
 *   • Who you're impersonating (the target's name + email)
 *   • Who YOU are (the admin email — important after page refresh)
 *   • A one-click "Return to admin" button that swaps the JWT back without a
 *     server round-trip.
 *
 * Hidden when no impersonation session is active. Sits ABOVE the sidebar so
 * it's impossible to miss — a published post under the wrong account is the
 * exact failure mode this banner is built to prevent.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const ImpersonationBanner = () => {
  const { getImpersonationMeta, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  // Force a re-render when the meta changes (after impersonate() / stop).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'admin_stash_meta') setTick((t) => t + 1);
    };
    window.addEventListener('storage', onStorage);
    // Also re-render every 30s in case the tab missed an event.
    const i = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(i);
    };
  }, []);

  const meta = getImpersonationMeta();
  if (!meta) return null;

  const handleReturn = () => {
    const ok = stopImpersonation();
    if (ok) {
      toast.success(`Returned to your admin session (${meta.admin_email})`);
      // Force a hard navigation back to /admin so any stale page data is dropped.
      // (Components that read /auth/me will refetch under the restored token.)
      navigate('/admin', { replace: true });
    } else {
      toast.error('No stashed admin session — please log in again.');
    }
  };

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500 text-amber-950 shadow-md"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Eye className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm truncate">
          You are viewing the app as{' '}
          <strong>{meta.target_name || meta.target_email}</strong>
          <span className="hidden sm:inline">
            {' '}
            (<span className="font-mono text-xs">{meta.target_email}</span>) — your admin session{' '}
            <span className="font-mono text-xs">{meta.admin_email}</span> is paused.
          </span>
        </span>
      </div>
      <button
        type="button"
        onClick={handleReturn}
        data-testid="impersonation-return-btn"
        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-950 text-amber-100 text-xs font-semibold hover:bg-amber-900 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
        Return to admin
      </button>
    </div>
  );
};
